(() => {
  if (customElements.get('afterlight-aperture')) return;
  const glassThickness = 0.6;
  const glassRoughness = 0.32;
  const vertexSource = `
    attribute vec3 aPosition;
    attribute vec3 aNormal;
    uniform float uTurn;
    uniform float uAspect;
    varying vec3 vNormal;
    varying vec3 vPosition;
    varying vec3 vLocalPosition;
    varying vec3 vLocalNormal;
    varying vec3 vLocalEye;
    void main() {
      float x = -.523599 + sin(uTurn) * .06;
      float y = .244346 + uTurn;
      float z = .401426;
      mat3 rx = mat3(1.,0.,0.,0.,cos(x),sin(x),0.,-sin(x),cos(x));
      mat3 ry = mat3(cos(y),0.,-sin(y),0.,1.,0.,sin(y),0.,cos(y));
      mat3 rz = mat3(cos(z),sin(z),0.,-sin(z),cos(z),0.,0.,0.,1.);
      mat3 rotation = rz * rx * ry;
      vec3 modelScale = vec3(1.,1.,${glassThickness.toFixed(2)} / .4);
      vLocalPosition = aPosition * modelScale;
      vLocalNormal = normalize(aNormal / modelScale);
      vPosition = rotation * vLocalPosition;
      vNormal = rotation * vLocalNormal;
      vec3 worldEye = vec3(0.,0.,4.) - vPosition;
      vLocalEye = vec3(dot(rotation[0],worldEye),dot(rotation[1],worldEye),dot(rotation[2],worldEye));
      gl_Position = vec4(vPosition.x / (1.6 * uAspect), (vPosition.y + sin(uTurn * 2.) * .035) / 1.6, -vPosition.z * .3, 1.);
    }
  `;
  const fragmentSource = `
    precision mediump float;
    varying vec3 vNormal;
    varying vec3 vPosition;
    varying vec3 vLocalPosition;
    varying vec3 vLocalNormal;
    varying vec3 vLocalEye;
    vec3 rainbow(float t) {
      t = fract(t) * 6.;
      vec3 blue = vec3(0.,.53,.92);
      vec3 green = vec3(.10,.73,.45);
      vec3 yellow = vec3(1.,.94,.12);
      vec3 orange = vec3(1.,.32,.12);
      vec3 pink = vec3(1.,0.,.40);
      vec3 violet = vec3(.45,0.,1.);
      if (t < 1.) return mix(blue,green,t);
      if (t < 2.) return mix(green,yellow,t - 1.);
      if (t < 3.) return mix(yellow,orange,t - 2.);
      if (t < 4.) return mix(orange,pink,t - 3.);
      if (t < 5.) return mix(pink,violet,t - 4.);
      return mix(violet,blue,t - 5.);
    }
    const float roughness = ${glassRoughness.toFixed(2)};
    const float glassIor = 1.51;
    const float dispersion = .30;
    const float opticalThickness = ${glassThickness.toFixed(2)};
    vec3 tintAt(vec3 position) {
      float angle = atan(position.y,position.x) / 6.283185 + .25 + position.z * .045;
      float blur = roughness * .16;
      return rainbow(angle) * .5 + rainbow(angle - blur) * .25 + rainbow(angle + blur) * .25;
    }
    vec3 studio(vec3 r) {
      vec3 color = mix(vec3(.025,.045,.045),vec3(.22,.29,.25),smoothstep(-.2,.8,r.y));
      float ribbon = 1. - smoothstep(.18,.42,abs(r.x + r.y * .3 - .12));
      color += ribbon * vec3(.24,.48,.43);
      float teal = pow(max(0.,dot(r,normalize(vec3(-1.,.5,1.)))),10.);
      float warm = pow(max(0.,dot(r,normalize(vec3(1.,.7,.3)))),14.);
      float softbox = pow(max(0.,dot(r,normalize(vec3(.2,1.,1.)))),34.);
      color += teal * vec3(.10,.85,.80) + warm * vec3(1.,.72,.45) + softbox * vec3(1.5);
      float lightStrip = pow(max(0.,1. - abs(r.y - r.x * .4 + .35) * 7.),6.);
      color += lightStrip * vec3(1.3);
      return color;
    }
    vec3 frostedStudio(vec3 ray) {
      vec3 tangent = normalize(cross(ray,abs(ray.y) < .9 ? vec3(0.,1.,0.) : vec3(1.,0.,0.)));
      vec3 bitangent = cross(ray,tangent);
      float blur = roughness * .8;
      return studio(ray) * .2 + (
        studio(normalize(ray + tangent * blur)) + studio(normalize(ray - tangent * blur)) +
        studio(normalize(ray + bitangent * blur)) + studio(normalize(ray - bitangent * blur))
      ) * .2;
    }
    void main() {
      vec3 n = normalize(vNormal);
      if (!gl_FrontFacing) n = -n;
      vec3 eye = normalize(vec3(0.,0.,4.) - vPosition);
      float facing = clamp(dot(n,eye),0.,1.);
      float reflectance = pow((glassIor - 1.) / (glassIor + 1.),2.);
      float fresnel = reflectance + (1. - reflectance) * pow(1. - facing,4.);
      vec3 localNormal = normalize(vLocalNormal);
      if (!gl_FrontFacing) localNormal = -localNormal;
      vec3 localEye = normalize(vLocalEye);
      float spread = dispersion * .10;
      vec3 redRay = refract(-localEye,localNormal,1. / (glassIor - spread));
      vec3 greenRay = refract(-localEye,localNormal,1. / glassIor);
      vec3 blueRay = refract(-localEye,localNormal,1. / (glassIor + spread));
      float pathLength = opticalThickness / max(abs(greenRay.z),.25);
      vec3 spectrum = vec3(
        tintAt(vLocalPosition + redRay * pathLength).r,
        tintAt(vLocalPosition + greenRay * pathLength).g,
        tintAt(vLocalPosition + blueRay * pathLength).b
      );
      vec3 refractedLight = vec3(
        frostedStudio(refract(-eye,n,1. / (glassIor - spread))).r,
        frostedStudio(refract(-eye,n,1. / glassIor)).g,
        frostedStudio(refract(-eye,n,1. / (glassIor + spread))).b
      );
      vec3 absorption = exp(-(vec3(1.) - spectrum) * pathLength * .8);
      vec3 reflected = studio(reflect(-eye,n));
      vec3 reflectedLight = pow(reflected / (reflected + vec3(.65)),vec3(1./2.2));
      float highlight = pow(max(0.,dot(reflect(-eye,n),normalize(vec3(.2,1.,1.)))),18.);
      vec3 transmittedLight = spectrum * (.82 + facing * .18) * absorption;
      transmittedLight += refractedLight * (.20 + (1. - facing) * .12);
      transmittedLight = mix(transmittedLight,vec3(.78,.82,.88),roughness * facing * .34);
      vec3 glass = mix(transmittedLight,reflectedLight,.12 + fresnel * .65);
      glass += vec3(1.) * highlight * .8;
      float alpha = min(.95,.40 + fresnel * .36 + highlight * .25);
      gl_FragColor = vec4(glass,alpha);
    }
  `;
  class AfterlightAperture extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;
      this.canvas = this.querySelector('canvas');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.nearby = false;
      this.turn = 0;
      this.onVisibility = () => this.update();
      this.onMotion = () => {
        this.classList.toggle('is-still',this.motion.matches);
        this.prepare();
        this.update();
      };
      this.motion.addEventListener('change',this.onMotion);
      document.addEventListener('visibilitychange',this.onVisibility);
      this.nearObserver = new IntersectionObserver(entries => {
        this.nearby = entries[0].isIntersecting;
        this.prepare();
      },{rootMargin:'200px'});
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.update();
      });
      this.nearObserver.observe(this);
      this.observer.observe(this);
      this.onMotion();
    }
    async prepare() {
      if (!this.nearby || this.motion.matches || this.loading || this.gl || this.failed) return;
      this.loading = true;
      this.dataset.renderState = 'loading';
      const controller = this.controller = new AbortController();
      try {
        const { default: mesh } = await import(this.dataset.mesh);
        if (!this.isConnected || controller.signal.aborted) return;
        const gl = this.canvas.getContext('webgl',{alpha:true,antialias:true,powerPreference:'low-power'});
        if (!gl) throw new Error('WebGL unavailable');
        this.gl = gl;
        this.buffers = [];
        this.shaders = [];
        const compile = (type,source) => {
          const shader = gl.createShader(type);
          this.shaders.push(shader);
          gl.shaderSource(shader,source);
          gl.compileShader(shader);
          if (!gl.getShaderParameter(shader,gl.COMPILE_STATUS)) throw new Error('Shader unavailable');
          return shader;
        };
        const program = this.program = gl.createProgram();
        gl.attachShader(program,compile(gl.VERTEX_SHADER,vertexSource));
        gl.attachShader(program,compile(gl.FRAGMENT_SHADER,fragmentSource));
        gl.linkProgram(program);
        if (!gl.getProgramParameter(program,gl.LINK_STATUS)) throw new Error('Program unavailable');
        gl.useProgram(program);
        const bytes = Uint8Array.from(atob(mesh.data),character => character.charCodeAt(0));
        const data = new DataView(bytes.buffer);
        const positions = new Float32Array(mesh.vertexCount * 3);
        const normals = new Float32Array(mesh.vertexCount * 3);
        for (let i = 0; i < mesh.vertexCount; i++) {
          for (let j = 0; j < 3; j++) {
            positions[i * 3 + j] = data.getInt16(i * 12 + j * 2,true) / 32767 * mesh.scale;
            normals[i * 3 + j] = data.getInt16(i * 12 + 6 + j * 2,true) / 32767;
          }
        }
        const indices = new Uint16Array(mesh.indexCount);
        for (let i = 0; i < indices.length; i++) indices[i] = data.getUint16(mesh.vertexCount * 12 + i * 2,true);
        for (const [name,values] of [['aPosition',positions],['aNormal',normals]]) {
          const buffer = gl.createBuffer();
          this.buffers.push(buffer);
          gl.bindBuffer(gl.ARRAY_BUFFER,buffer);
          gl.bufferData(gl.ARRAY_BUFFER,values,gl.STATIC_DRAW);
          const location = gl.getAttribLocation(program,name);
          gl.enableVertexAttribArray(location);
          gl.vertexAttribPointer(location,3,gl.FLOAT,false,0,0);
        }
        const indexBuffer = gl.createBuffer();
        this.buffers.push(indexBuffer);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER,indexBuffer);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER,indices,gl.STATIC_DRAW);
        this.count = indices.length;
        this.turnLocation = gl.getUniformLocation(program,'uTurn');
        this.aspectLocation = gl.getUniformLocation(program,'uAspect');
        gl.enable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA,gl.ONE_MINUS_SRC_ALPHA);
        gl.enable(gl.CULL_FACE);
        gl.clearColor(0,0,0,0);
        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(this);
        this.onContextLost = event => {
          event.preventDefault();
          this.failed = true;
          this.stop();
          this.classList.remove('is-ready');
        };
        this.canvas.addEventListener('webglcontextlost',this.onContextLost);
        this.resize();
        this.classList.add('is-ready');
        this.dataset.renderState = 'ready';
        this.update();
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.failed = true;
          this.dataset.renderState = 'fallback';
          console.warn('Afterlight aperture: using still image.',error.message);
          this.classList.remove('is-ready');
          this.release();
        }
      } finally {
        this.loading = false;
      }
    }
    resize() {
      if (!this.gl) return;
      const rect = this.getBoundingClientRect();
      const ratio = Math.min(devicePixelRatio || 1,1.5);
      this.canvas.width = Math.max(1,Math.round(rect.width * ratio));
      this.canvas.height = Math.max(1,Math.round(rect.height * ratio));
      this.gl.viewport(0,0,this.canvas.width,this.canvas.height);
      this.gl.uniform1f(this.aspectLocation,this.canvas.width / this.canvas.height);
      this.draw();
    }
    draw() {
      if (!this.gl || this.failed) return;
      this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT);
      this.gl.uniform1f(this.turnLocation,this.dataset.motion === 'hero' ? Math.sin(this.turn) * .24 : this.turn);
      this.gl.depthMask(false);
      this.gl.cullFace(this.gl.FRONT);
      this.gl.drawElements(this.gl.TRIANGLES,this.count,this.gl.UNSIGNED_SHORT,0);
      this.gl.cullFace(this.gl.BACK);
      this.gl.drawElements(this.gl.TRIANGLES,this.count,this.gl.UNSIGNED_SHORT,0);
      this.gl.depthMask(true);
    }
    update() {
      const animate = this.gl && !this.failed && this.visible && !document.hidden && !this.motion.matches;
      if (!animate) { this.stop(); return; }
      if (this.frame) return;
      this.dataset.animationState = 'running';
      this.lastTime = null;
      const tick = time => {
        if (this.lastTime === null) this.lastTime = time;
        const delta = time - this.lastTime;
        if (delta >= (this.frameInterval || 1000 / 30)) {
          this.turn = (this.turn + Math.min(delta,100) * .00012) % (Math.PI * 2);
          this.lastTime = time;
          this.draw();
        }
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    stop() {
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.dataset.animationState = 'paused';
    }
    release() {
      if (!this.gl) return;
      for (const buffer of this.buffers || []) this.gl.deleteBuffer(buffer);
      for (const shader of this.shaders || []) this.gl.deleteShader(shader);
      if (this.program) this.gl.deleteProgram(this.program);
      this.gl = null;
    }
    disconnectedCallback() {
      this.stop();
      this.controller?.abort();
      this.observer?.disconnect();
      this.nearObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.motion?.removeEventListener('change',this.onMotion);
      document.removeEventListener('visibilitychange',this.onVisibility);
      this.canvas?.removeEventListener('webglcontextlost',this.onContextLost);
      this.release();
      this.observer = null;
      this.classList.remove('is-ready');
    }
  }
  customElements.define('afterlight-aperture',AfterlightAperture);
  const gradientVertex = `
    attribute vec2 aPosition;
    varying vec2 vUv;
    void main() {
      vUv = aPosition * .5 + .5;
      gl_Position = vec4(aPosition,0.,1.);
    }
  `;
  const gradientFragment = `
    precision highp float;
    varying vec2 vUv;
    uniform float uTime;
    uniform float uAspect;
    uniform vec4 uVariation;
    uniform vec3 uBase;
    uniform vec3 uColorA;
    uniform vec3 uColorB;
    uniform vec3 uColorC;
    float pool(vec2 p,vec2 center,float size) {
      vec2 offset = p - center;
      return exp(-dot(offset,offset) * size);
    }
    void main() {
      float t = uTime * 2. + uVariation.x;
      vec2 p = (vUv - .5) * vec2(min(uAspect,2.),1.);
      p = vec2(p.x * uVariation.y - p.y * uVariation.z,p.x * uVariation.z + p.y * uVariation.y);
      p += vec2(sin(p.y * 4. + t),cos(p.x * 3. - t)) * uVariation.w;
      float blue = pool(p,vec2(-.45 + sin(t) * .30,-.18 + cos(t) * .16),3.5);
      float violet = pool(p,vec2(.45 + cos(t) * .22,.25 + sin(t) * .20),4.5);
      float teal = pool(p,vec2(sin(t) * .35,-.38 + cos(t) * .18),6.);
      float shadow = pool(p,vec2(.12 + cos(t) * .20,-.05),8.);
      vec3 color = uBase;
      color += blue * uColorA + violet * uColorB + teal * uColorC;
      color *= 1. - shadow * .45;
      float grain = fract(sin(dot(gl_FragCoord.xy,vec2(12.9898,78.233))) * 43758.5453) - .5;
      color += grain * .022;
      gl_FragColor = vec4(color,1.);
    }
  `;
  class AfterlightMeshGradient extends AfterlightAperture {
    constructor() {
      super();
      const angle = Math.random() * Math.PI * 2;
      this.variation = new Float32Array([
        Math.random() * Math.PI * 2,
        Math.cos(angle),
        Math.sin(angle),
        .16 + Math.random() * .12
      ]);
    }
    prepare() {
      if (!this.nearby || this.motion.matches || this.gl || this.failed) return;
      try {
        const gl = this.canvas.getContext('webgl',{alpha:false,antialias:false,powerPreference:'low-power'});
        if (!gl) throw new Error('WebGL unavailable');
        this.gl = gl;
        this.buffers = [];
        this.shaders = [];
        const program = this.program = gl.createProgram();
        for (const [type,source] of [[gl.VERTEX_SHADER,gradientVertex],[gl.FRAGMENT_SHADER,gradientFragment]]) {
          const shader = gl.createShader(type);
          this.shaders.push(shader);
          gl.shaderSource(shader,source);
          gl.compileShader(shader);
          if (!gl.getShaderParameter(shader,gl.COMPILE_STATUS)) throw new Error('Shader unavailable');
          gl.attachShader(program,shader);
        }
        gl.linkProgram(program);
        if (!gl.getProgramParameter(program,gl.LINK_STATUS)) throw new Error('Program unavailable');
        gl.useProgram(program);
        const buffer = gl.createBuffer();
        this.buffers.push(buffer);
        gl.bindBuffer(gl.ARRAY_BUFFER,buffer);
        gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]),gl.STATIC_DRAW);
        const position = gl.getAttribLocation(program,'aPosition');
        gl.enableVertexAttribArray(position);
        gl.vertexAttribPointer(position,2,gl.FLOAT,false,0,0);
        const palettes = {
          rainbow: [[.025,.018,.035],[.21,.065,.025],[.05,.08,.22],[.12,.17,.035]],
          blue: [[.008,.022,.045],[.02,.075,.25],[.06,.055,.20],[.01,.12,.20]],
          mint: [[.008,.03,.025],[.02,.19,.14],[.04,.12,.07],[.015,.14,.18]],
          violet: [[.02,.012,.04],[.10,.035,.24],[.16,.035,.20],[.07,.04,.21]]
        };
        const palette = palettes[this.dataset.palette] || [[.016,.020,.048],[.035,.085,.235],[.12,.045,.20],[.01,.10,.11]];
        ['uBase','uColorA','uColorB','uColorC'].forEach((name,index) => {
          gl.uniform3fv(gl.getUniformLocation(program,name),palette[index]);
        });
        gl.uniform4fv(gl.getUniformLocation(program,'uVariation'),this.variation);
        this.turnLocation = gl.getUniformLocation(program,'uTime');
        this.aspectLocation = gl.getUniformLocation(program,'uAspect');
        this.frameInterval = 1000 / 20;
        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(this);
        this.onContextLost = event => {
          event.preventDefault();
          this.failed = true;
          this.stop();
          this.classList.remove('is-ready');
          this.dataset.renderState = 'fallback';
        };
        this.canvas.addEventListener('webglcontextlost',this.onContextLost);
        this.resize();
        this.classList.add('is-ready');
        this.dataset.renderState = 'ready';
        this.update();
      } catch (error) {
        this.failed = true;
        this.dataset.renderState = 'fallback';
        this.classList.remove('is-ready');
        this.release();
      }
    }
    resize() {
      if (!this.gl) return;
      const rect = this.getBoundingClientRect();
      const scale = Math.min(devicePixelRatio || 1,2,1600 / Math.max(1,rect.width));
      this.canvas.width = Math.max(1,Math.round(rect.width * scale));
      this.canvas.height = Math.max(1,Math.round(rect.height * scale));
      this.gl.viewport(0,0,this.canvas.width,this.canvas.height);
      this.gl.uniform1f(this.aspectLocation,this.canvas.width / this.canvas.height);
      this.draw();
    }
    draw() {
      if (!this.gl || this.failed) return;
      this.gl.uniform1f(this.turnLocation,this.turn);
      this.gl.drawArrays(this.gl.TRIANGLES,0,6);
    }
  }
  customElements.define('afterlight-mesh-gradient',AfterlightMeshGradient);

})();
